import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  useMemo,
  type MouseEvent,
} from 'react'
import lodashThrottle from 'lodash.throttle'
import lodashDebounce from 'lodash.debounce'

import {ColumnSize, ResizingColumn} from 'interfaces'
import Logger from 'services/Logger'

export interface Column {
  name: string
  dataKey: string
  widthPercentage: number
}

interface UseTableResizeProps {
  columns: Column[]
  storageKey: string
  minColumnWidth?: number
  fixedColumnWidths?: Record<string, number>
  additionalOffset?: number
}

/**
 * @description Custom hook for handling table column resizing
 *
 * @param columns - Array of column objects
 * @param storageKey - Key to use for storing column sizes in session storage
 * @param minColumnWidth - Minimum width for columns below which resizing is disabled
 * @param fixedColumnWidths - Object with fixed column widths
 * @param additionalOffset - Additional offset to account for in available width for empty columns
 *
 * @summary
 * - Calculates initial column sizes based on available space and column width percentages.
 * - Persists column sizes to session storage.
 * - Handles the start, resizing and end of a column resize operation.
 * - Gets width for a specific column.
 * - Handles zoom/resize events.
 * - Save column sizes when they change and persist them.
 */
export function useTableResize({
  columns,
  storageKey,
  minColumnWidth = 100,
  fixedColumnWidths = {},
  additionalOffset = 0,
}: UseTableResizeProps) {
  const [columnSizes, setColumnSizes] = useState<ColumnSize[]>([])
  const [isResizing, setIsResizing] = useState<boolean>(false)

  const resizingColumnRef = useRef<ResizingColumn | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const isInitializedRef = useRef(false)

  // Persist column sizes to session storage
  const persistColumnSizes = useCallback(
    (sizes: ColumnSize[]) => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(sizes))
      } catch (error: unknown) {
        Logger.LogError(error as Error)
        console.error('Error persisting column sizes', error)
      }
    },
    [storageKey],
  )

  // Memoize the debounced persisting function
  const debouncedPersistSizes = useMemo(
    () =>
      lodashDebounce((sizes: ColumnSize[]) => persistColumnSizes(sizes), 300),
    [persistColumnSizes],
  )

  /**
   * @description Calculate initial column widths based on available space
   * and column width percentages
   */
  const calculateColumnSizes = useCallback(() => {
    if (!tableRef.current) return

    // Prevent accidental recursive calculations
    if (isInitializedRef.current) return

    try {
      // Check session storage for stored columnSizes data
      const storedSizes = sessionStorage.getItem(storageKey)
      if (storedSizes) {
        try {
          const parsedSizes = JSON.parse(storedSizes) as ColumnSize[]
          // If the stored sizes match the current columns length, reuse them
          if (parsedSizes.length === columns.length) {
            setColumnSizes(parsedSizes)
            isInitializedRef.current = true
            return
          }
        } catch (error: unknown) {
          Logger.LogError(error as Error)
          console.error('Error parsing stored column sizes', error)
        }
      }

      const tableWidth = tableRef.current.getBoundingClientRect().width

      // Calculate total fixed width (columns with defined fixed widths)
      const totalFixedWidth = Object.values(fixedColumnWidths).reduce(
        (sum, width) => sum + width,
        0,
      )

      // Calculate available width for resizable columns
      const availableWidth = tableWidth - totalFixedWidth - additionalOffset

      // Calculate total width percentage for distributing the available space
      const totalPercentage = columns.reduce(
        (acc, col) => acc + col.widthPercentage,
        0,
      )

      // Initial calculation of column sizes based on percentages
      let initialSizes = columns.map(column => {
        // If column has fixed width, use that width
        if (fixedColumnWidths[column.dataKey]) {
          return {
            id: column.dataKey,
            width: fixedColumnWidths[column.dataKey],
          }
        }

        // Otherwise calculate width based on percentage
        const calculatedWidth = Math.max(
          (availableWidth * column.widthPercentage) / totalPercentage,
          minColumnWidth,
        )

        return {
          id: column.dataKey,
          width: calculatedWidth,
        }
      })

      // Calculate total width of all columns after initial sizing
      const totalCalculatedWidth = initialSizes.reduce(
        (acc, col) => acc + col.width,
        0,
      )

      // If total width exceeds available width, adjust columns proportionally
      if (totalCalculatedWidth > availableWidth + totalFixedWidth) {
        // Only adjust columns that aren't fixed and exceed minimum width
        const adjustableColumns = initialSizes.filter(
          col => !fixedColumnWidths[col.id] && col.width > minColumnWidth,
        )

        if (adjustableColumns.length > 0) {
          const excessWidth =
            totalCalculatedWidth - (availableWidth + totalFixedWidth)
          const adjustableTotalWidth = adjustableColumns.reduce(
            (acc, col) => acc + col.width,
            0,
          )

          // Proportionally reduce the width of adjustable columns
          initialSizes = initialSizes.map(column => {
            if (
              !fixedColumnWidths[column.id] &&
              column.width > minColumnWidth
            ) {
              const reductionRatio = column.width / adjustableTotalWidth
              const widthReduction = excessWidth * reductionRatio
              return {
                ...column,
                width: Math.max(column.width - widthReduction, minColumnWidth),
              }
            }
            return column
          })
        }
      }

      setColumnSizes(initialSizes)
      isInitializedRef.current = true
    } catch (error: unknown) {
      Logger.LogError(error as Error)
      console.error('Error calculating column sizes', error)
    }
  }, [columns, minColumnWidth, fixedColumnWidths, storageKey, additionalOffset])

  /**
   * @description Handle the start of a column resize operation
   */
  const handleResizeStart = useCallback(
    (columnId: string, event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (!tableRef.current) return

      // Skip if this is a fixed column
      if (fixedColumnWidths[columnId]) return

      // Set isResizing to `true`
      setIsResizing(true)

      // Set the global pointer style to ensure the pointer doesn't change
      document.body.style.cursor = 'col-resize'

      // Add the active-resizing class to the resize handle
      const resizeHandleEl = event.currentTarget as HTMLElement
      resizeHandleEl.classList.add('active-resizing')

      const currentHeader = tableRef.current.querySelector(
        `th[data-column-id="${columnId}"]`,
      )

      if (!currentHeader) return

      resizingColumnRef.current = {
        id: columnId,
        startX: event.clientX,
        startWidth: currentHeader.getBoundingClientRect().width,
        widthAdjustmentApplied: false,
      }

      // Clear any existing event listeners on the DOM
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)

      // Attach fresh event listeners on the DOM
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
    },
    [fixedColumnWidths],
  )

  /**
   * @description Handles the resizing of a column
   */
  const handleResizeMove = useCallback(
    lodashThrottle((event: globalThis.MouseEvent) => {
      if (!resizingColumnRef.current) return

      const {startX, startWidth, id, widthAdjustmentApplied} =
        resizingColumnRef.current
      const delta = event.clientX - startX
      const newWidth = startWidth + delta

      const columnIndex = columns.findIndex(col => col.dataKey === id)
      if (columnIndex === -1) return

      // Check if we're at or below minimum width
      if (newWidth <= minColumnWidth && !widthAdjustmentApplied) {
        resizingColumnRef.current.widthAdjustmentApplied = true

        setColumnSizes(prevSizes => {
          // Create a new array with all columns at their current sizes
          const newSizes = [...prevSizes]

          // Set the current column to min width
          const sizeIndex = newSizes.findIndex(s => s.id === id)
          if (sizeIndex !== -1) {
            newSizes[sizeIndex] = {
              ...newSizes[sizeIndex],
              width: minColumnWidth,
            }
          }

          return newSizes
        })
      } else if (newWidth > minColumnWidth) {
        const header = document.querySelector(`th[data-column-id="${id}"]`)
        if (header instanceof HTMLElement) {
          header.style.width = `${newWidth}px`
        }

        setColumnSizes(prevSizes => {
          const newSizes = [...prevSizes]

          // Find the column by id and update its width
          const sizeIndex = newSizes.findIndex(s => s.id === id)
          if (sizeIndex !== -1) {
            newSizes[sizeIndex] = {
              ...newSizes[sizeIndex],
              width: newWidth,
            }
          }

          return newSizes
        })
      }

      event.preventDefault()
      event.stopPropagation()
    }, 16),
    [columns, minColumnWidth],
  )

  /**
   * @description Clean up after column resizing
   */
  const handleResizeEnd = useCallback(() => {
    // Reset the global pointer style when resizing ends
    document.body.style.cursor = ''

    // Reset the resizing state
    setIsResizing(false)

    // Remove the active-resizing class from the resize handle
    const activeHandles = document.getElementsByClassName('active-resizing')
    while (activeHandles.length) {
      activeHandles[0].classList.remove('active-resizing')
    }

    // Clear the resizing column reference
    resizingColumnRef.current = null

    // Cleanup the added listeners
    document.removeEventListener('mousemove', handleResizeMove)
    document.removeEventListener('mouseup', handleResizeEnd)
  }, [handleResizeMove])

  /**
   * @description Get width for a specific column
   */
  const getColumnWidth = useCallback(
    (columnId: string) => {
      if (fixedColumnWidths[columnId]) return `${fixedColumnWidths[columnId]}px`

      const column = columnSizes.find(col => col.id === columnId)
      if (!column) return `${minColumnWidth}px`

      return `${column.width}px`
    },
    [columnSizes, minColumnWidth, fixedColumnWidths],
  )

  // Handle zoom/resize events
  useEffect(() => {
    const handleResize = () => {
      // Only recalculate if dimensions actually changed
      isInitializedRef.current = false
      calculateColumnSizes()
    }

    const debouncedResize = lodashDebounce(handleResize, 250)
    window.addEventListener('resize', debouncedResize)
    return () => {
      window.removeEventListener('resize', debouncedResize)
      debouncedResize.cancel()
    }
  }, [calculateColumnSizes])

  // Initial setup to calculate column sizes
  useLayoutEffect(() => {
    if (!isInitializedRef.current) {
      calculateColumnSizes()
    }
  }, [calculateColumnSizes])

  // Save column sizes when they change
  useEffect(() => {
    // Only persist if we have valid sizes and it's not the initial render
    if (columnSizes.length && isInitializedRef.current) {
      debouncedPersistSizes(columnSizes)
    }
  }, [columnSizes, debouncedPersistSizes])

  return {
    tableRef,
    columnSizes,
    isResizing,
    getColumnWidth,
    handleResizeStart,
    handleResizeEnd,
    calculateColumnSizes,
    setColumnSizes,
  }
}
